import { JSX, ReactNode, forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { colors, space } from '@ardoq/design-tokens';
import {
  aiGradientBorderCSS,
  aiSubtleGradientCSS,
  aiSubtleGradientDarkCSS,
  errorCSS,
  focusableCSS,
  warningCSS,
} from '@ardoq/style-helpers';
import { capitalize, isNumber, isString } from 'lodash';
import {
  ICON_SELECTOR,
  IconSize,
  SpinnerIcon,
  AiIconGradientAnimated,
  AI_ICON_GRADIENT_ANIMATED_SELECTOR as AI_GRADIENT_SELECTOR,
  animateAiIconGradientFill,
} from '@ardoq/icons';
import { ButtonProps, ButtonSize, ButtonStyleProps, ButtonType } from './types';
import { composeCssStatesColorBlock } from './utils';
import { BUTTON_COLOR_MAP } from './const';
import { ButtonShadowVariables } from './shadows';
import { Text } from '@ardoq/typography';
import { UnstyledLink } from '@ardoq/link';
import { DEFAULT_ARDOQ_I18NLOCALE, t } from '@ardoq/i18n';

const isSmall = (buttonSize?: ButtonSize) => buttonSize === ButtonSize.SMALL;

const ButtonLoadingIconWrapper = styled.div`
  position: absolute;
  width: 100%;
  left: 0;
  top: calc(50% - 12px);
`;

const OPACITY_TRANSITION = 'opacity 0.2s ease-in-out';

export const UnstyledButton = styled.button`
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  border: none;
  padding: 0;
  margin: 0;
  background: transparent;
  box-shadow: none;
  /* Inherit font properties from parent element */
  font: inherit; /* Includes family, size, weight, line-height, etc. */
  /* Inherit text color from parent element */
  color: inherit;
  /* Inherit text alignment */
  text-align: inherit;
  text-decoration: none;
  /* Prevent any default text transformation */
  text-transform: none;
  outline: none;

  cursor: pointer;
  ${focusableCSS};
`;
export const buttonTypography = css<{ $buttonSize?: ButtonSize }>`
  font-size: 13px;
  font-weight: 500;
  line-height: ${({ $buttonSize }) => (isSmall($buttonSize) ? '20px' : '24px')};
`;

export const buttonStyleCSS = css<ButtonStyleProps>`
  ${buttonTypography};
  cursor: pointer;
  border-radius: 6px;
  min-height: ${({ $buttonSize }) => (isSmall($buttonSize) ? '28px' : `34px`)};
  padding: ${({ $buttonSize }) =>
    isSmall($buttonSize)
      ? `${space.xsmall} ${space.xxsmall}`
      : `0 ${space.xsmall}`};
  border: 1px solid transparent;
  transition: all 0.2s ease-in-out;
  vertical-align: bottom;
  user-select: none;

  max-width: 100%;

  ${focusableCSS};

  ${({ $buttonType, $isActive }) =>
    composeCssStatesColorBlock(BUTTON_COLOR_MAP[$buttonType], $isActive)};

  ${({ $isDisabled }) =>
    $isDisabled
      ? css`
          pointer-events: none;
          &[data-tooltip-text] {
            pointer-events: revert;
          }
        `
      : ''}

  ${({ $isFlexible }) =>
    $isFlexible &&
    css`
      width: 100%;
      ${ButtonContent} {
        justify-content: center;
      }
    `}

  ${({ $isLoading }) => css`
    ${$isLoading &&
    css`
      pointer-events: none;
    `}
    ${ButtonContent} {
      transition: ${OPACITY_TRANSITION};
      opacity: ${$isLoading ? 0 : 1};
    }
    ${ButtonLoadingIconWrapper} {
      transition: ${OPACITY_TRANSITION};
      opacity: ${$isLoading ? 1 : 0};
      pointer-events: none;
    }
  `}
  ${({ $hasError }) => $hasError && errorCSS}
  ${({ $hasError, $hasWarning }) => !$hasError && $hasWarning && warningCSS}
`;

const ButtonStyle = styled.button<ButtonStyleProps>`
  ${buttonStyleCSS};
`;

/**
 * BUTTON_SELECTOR
 */
export const BUTTON_SELECTOR = ButtonStyle.toString();

const ButtonText = styled.div``;

const ButtonContent = styled.div<ButtonStyleProps>`
  display: flex;
  align-items: center;

  ${({ $buttonSize, $isFlexible }) => css`
    ${ICON_SELECTOR},
    ${AI_GRADIENT_SELECTOR} {
      flex-shrink: 0;
      /* &:first-child {
        margin-left: ${isSmall($buttonSize) ? space.xxsmall : space.xsmall};
      }
      &:last-child {
        margin-right: ${isSmall($buttonSize) ? space.xxsmall : space.xsmall};
      } */
      margin: 0
        ${isSmall($buttonSize) && !$isFlexible ? space.xxsmall : space.xsmall};

      /*
        Case if Icon is the only button element (essentially it's IconButton).
        We want to have icon buttons exactly square that's why we are using this
        custom 2px spacing.
      */
      &:first-child:last-child {
        margin: 0 ${isSmall($buttonSize) ? '3px' : '2px'};
      }

      /*
        This is a special case where we set icon size
        with parent's styles. It's because button size
        designate icon size. That's why I have to use
        here !important (in order to overwite inline styles)
      */
      font-size: ${isSmall($buttonSize)
        ? IconSize.SMALL
        : IconSize.MEDIUM}px !important;
      width: ${isSmall($buttonSize)
        ? IconSize.SMALL
        : IconSize.MEDIUM}px !important;
      min-width: ${isSmall($buttonSize)
        ? IconSize.SMALL
        : IconSize.MEDIUM}px !important;
      height: ${isSmall($buttonSize)
        ? IconSize.SMALL
        : IconSize.MEDIUM}px !important;
    }

    ${ButtonText} {
      &:last-child {
        margin-right: ${space.small};
      }
      &:first-child {
        margin-left: ${space.small};
      }
      &:not(:first-child):not(:last-child) {
        margin: 0
          ${isSmall($buttonSize) || $isFlexible ? space.xxsmall : space.xsmall};
      }
      &:first-child:not(:last-child) {
        margin-right: ${isSmall($buttonSize) || $isFlexible
          ? space.xxsmall
          : space.xsmall};
      }
      &:last-child:not(:first-child) {
        margin-left: ${isSmall($buttonSize) || $isFlexible
          ? space.xxsmall
          : space.xsmall};
      }

      /* margin: 0
        ${isSmall($buttonSize) || $isFlexible ? space.xsmall : space.small}; */
    }
  `}
`;

const reactNodeToArray = (node: ReactNode) =>
  (Array.isArray(node) ? node : [node]).flat().filter(Boolean);

const wrapTextNodes = (node: ReactNode): ReactNode =>
  reactNodeToArray(node).map((node, index) =>
    isString(node) || isNumber(node) ? (
      <ButtonText key={index} as={Text} truncate>
        {node}
      </ButtonText>
    ) : (
      node
    )
  );

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      buttonSize = ButtonSize.REGULAR,
      isFlexible,
      hasWarning,
      hasError,
      buttonType = ButtonType.TERTIARY,
      isActive,
      isDisabled,
      isLoading,
      dataTestId,
      onClick,
      i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
      ...buttonProps
    },
    ref
  ) => {
    const hasOnClickHandler = Boolean(onClick);
    const ButtonElement =
      buttonType === ButtonType.AI ? AiButtonStyle : ButtonStyle;
    return (
      <>
        <ButtonShadowVariables />
        <ButtonElement
          ref={ref}
          {...(hasOnClickHandler ? { type: 'button' } : {})}
          {...buttonProps}
          // Since a disabled button is removed from the tab navigation, we use aria-disabled
          // instead for a more accessible experience. Since it doesn't prevent onclick events
          // we do a early return in the onClick handler if isLoading is true.
          {...(isLoading
            ? {
                'aria-disabled': true,
                'aria-label': t(
                  i18nLocale,
                  'button:loadingAriaLabel',
                  'loading'
                ),
                'aria-busy': true,
              }
            : {})}
          {...(isActive !== undefined ? { 'aria-pressed': isActive } : {})}
          onClick={evt => {
            if (isLoading) return;
            onClick?.(evt);
          }}
          $isActive={isActive}
          $isDisabled={isDisabled}
          $isLoading={isLoading}
          $buttonType={buttonType}
          $hasError={hasError}
          $hasWarning={hasWarning}
          $isFlexible={isFlexible}
          $buttonSize={buttonSize}
          disabled={isDisabled}
          data-test-id={dataTestId}
          data-testid={dataTestId}
        >
          <ButtonContent
            $buttonSize={buttonSize}
            $isFlexible={isFlexible}
            $buttonType={buttonType}
          >
            {wrapTextNodes(buttonProps.children)}
          </ButtonContent>
          {isLoading && (
            <ButtonLoadingIconWrapper>
              <SpinnerIcon />
            </ButtonLoadingIconWrapper>
          )}
        </ButtonElement>
      </>
    );
  }
);

type GenerateButtonWithPredefinedPropsArgs = {
  buttonType: ButtonType;
  buttonSize: ButtonSize;
};
const generateButtonWithPredefinedProps = ({
  buttonType,
  buttonSize,
}: GenerateButtonWithPredefinedPropsArgs) => {
  const ForwardedComponent = forwardRef<
    HTMLButtonElement,
    Omit<ButtonProps, 'buttonType'>
  >((buttonProps, ref) => (
    <Button
      ref={ref}
      buttonType={buttonType}
      buttonSize={buttonSize}
      {...buttonProps}
    />
  ));
  ForwardedComponent.displayName = `${capitalize(buttonType)}Button`;
  return ForwardedComponent;
};

/**
 * Tertiary buttons have less prominence than a primary button and slightly more
 * prominence than a ghost button. Tertiary buttons work well on their own or
 * as part of a button group.
 */
export const TertiaryButton = generateButtonWithPredefinedProps({
  buttonType: ButtonType.TERTIARY,
  buttonSize: ButtonSize.REGULAR,
});

/**
 * Secondary buttons have less visual prominence than their primary counterparts
 * due to their lower saturation. As a result, secondary buttons **are intended
 * to be used in conjunction with a primary button**. When used in a pair, the
 * secondary button executes the set’s negative action, like “Cancel” or “Back.”
 */
export const SecondaryButton = generateButtonWithPredefinedProps({
  buttonType: ButtonType.SECONDARY,
  buttonSize: ButtonSize.REGULAR,
});

/**
 * Primary button is used for the principal call to action on the page. Primary
 * button **should only appear once per screen** (the only exceptions are temporary
 * flows with primary actions). Primary button helps give the user focus and gives
 * them context behind the expected next step.
 */
export const PrimaryButton = generateButtonWithPredefinedProps({
  buttonType: ButtonType.PRIMARY,
  buttonSize: ButtonSize.REGULAR,
});

/**
 * DangerButton if actions could have destructive effects on the user’s data
 * (for example, delete or remove).
 */
export const DangerButton = generateButtonWithPredefinedProps({
  buttonType: ButtonType.DANGER,
  buttonSize: ButtonSize.REGULAR,
});

/**
 * SecondaryDangerButton is the low-emphasis counterpart to DangerButton. Use it
 * for destructive actions (delete, remove) that should not dominate the screen —
 * for example, alongside a primary confirm action, or when the destructive
 * action is secondary to the main flow.
 */
export const SecondaryDangerButton = generateButtonWithPredefinedProps({
  buttonType: ButtonType.SECONDARY_DANGER,
  buttonSize: ButtonSize.REGULAR,
});

/**
 * For cases that require less visual prominence than a default button,
 * use a ghost button. It’s best suited for low-hierarchy actions, as
 * the button blends into the background.
 */
export const GhostButton = generateButtonWithPredefinedProps({
  buttonType: ButtonType.GHOST,
  buttonSize: ButtonSize.REGULAR,
});

/**
 * SmallButton
 */
export const SmallButton = generateButtonWithPredefinedProps({
  buttonType: ButtonType.TERTIARY,
  buttonSize: ButtonSize.SMALL,
});

/**
 * SmallDangerButton if actions could have destructive effects on the user’s data
 * (for example, delete or remove).
 */
export const SmallDangerButton = generateButtonWithPredefinedProps({
  buttonType: ButtonType.DANGER,
  buttonSize: ButtonSize.SMALL,
});

/**
 * SecondaryDangerButton is the low-emphasis counterpart to DangerButton. Use it
 * for destructive actions (delete, remove) that should not dominate the screen —
 * for example, alongside a primary confirm action, or when the destructive
 * action is secondary to the main flow.
 */
export const SmallSecondaryDangerButton = generateButtonWithPredefinedProps({
  buttonType: ButtonType.SECONDARY_DANGER,
  buttonSize: ButtonSize.SMALL,
});

/**
 * Primary button is used for the principal call to action on the page. Primary
 * button **should only appear once per screen** (the only exceptions are temporary
 * flows with primary actions). Primary button helps give the user focus and gives
 * them context behind the expected next step.
 */
export const SmallPrimaryButton = generateButtonWithPredefinedProps({
  buttonType: ButtonType.PRIMARY,
  buttonSize: ButtonSize.SMALL,
});

/**
 * Secondary buttons have less visual prominence than their primary counterparts
 * due to their lower saturation. As a result, secondary buttons **are intended
 * to be used in conjunction with a primary button**. When used in a pair, the
 * secondary button executes the set’s negative action, like “Cancel” or “Back.”
 */
export const SmallSecondaryButton = generateButtonWithPredefinedProps({
  buttonType: ButtonType.SECONDARY,
  buttonSize: ButtonSize.SMALL,
});

/**
 * For cases that require less visual prominence than a default button,
 * use a ghost button. It’s best suited for low-hierarchy actions, as
 * the button blends into the background.
 */
export const SmallGhostButton = generateButtonWithPredefinedProps({
  buttonType: ButtonType.GHOST,
  buttonSize: ButtonSize.SMALL,
});

/**
 * Tertiary buttons have less prominence than a primary button and slightly more
 * prominence than a ghost button. Tertiary buttons work well on their own or
 * as part of a button group.
 */
export const SmallTertiaryButton = generateButtonWithPredefinedProps({
  buttonType: ButtonType.TERTIARY,
  buttonSize: ButtonSize.SMALL,
});

const StyledLinkButton = styled(UnstyledLink)<{
  $buttonType: ButtonType;
}>`
  ${buttonStyleCSS};

  transition:
    all 0.2s ease-in-out,
    padding 0s ease-in-out;

  align-items: center;
  display: inline-flex;
  outline: none;
  padding: 0 ${space.small};
  width: fit-content;
  gap: ${space.small};

  ${ICON_SELECTOR} {
    font-size: ${IconSize.MEDIUM}px !important;
  }
  /* Only icon */
  &:has(${ICON_SELECTOR}:only-child) {
    padding: 0 calc(${space.xxsmall} + ${space.xsmall});
  }
  /* First icon, with text after */
  &:has(${ICON_SELECTOR}:first-child:not(:last-child)) {
    padding-right: calc(${space.small} + ${space.xsmall});
  }
  /* Last icon, with text before */
  &:has(${ICON_SELECTOR}:last-child:not(:first-child)) {
    padding-left: calc(${space.small} + ${space.xsmall});
  }
  /* Both first and last are icons, with content in between */
  /* prettier-ignore */
  &:has(${ICON_SELECTOR}:first-child):has(${ICON_SELECTOR}:last-child:not(:only-child)) {
    padding: 0 ${space.small};
  }
`;

export const LinkButton = ({
  buttonType,
  children,
  ...props
}: { buttonType?: ButtonType } & JSX.IntrinsicElements['a']) => (
  <>
    <ButtonShadowVariables />
    <StyledLinkButton
      {...props}
      $buttonType={buttonType ?? ButtonType.TERTIARY}
    >
      {wrapTextNodes(children)}
    </StyledLinkButton>
  </>
);

// Animated gradient border - smooth flowing gradient
const animateBackgroundPosition = animateAiIconGradientFill;

const AiButtonStyle = styled(ButtonStyle)<ButtonStyleProps>`
  position: relative;

  ${AI_GRADIENT_SELECTOR} {
    color: ${colors.orange500};
  }

  ${AI_GRADIENT_SELECTOR}::after {
    opacity: 0;
    animation: none;
  }

  ${aiGradientBorderCSS}

  &:focus-visible,
  &.focus {
    outline: 2px solid ${colors.blue400};
  }

  &:focus-visible::before,
  &.focus::before {
    inset: 0;
    border-radius: 6px;
    box-shadow: none;
  }

  /* Gradient background as pseudo-element for smooth fade-in */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 6px;
    background: ${aiSubtleGradientCSS()};
    opacity: 0;
    z-index: 0;
    pointer-events: none;
    transition: 0.3s ease-out;
  }

  &:active,
  &[aria-pressed='true'] {
    background: ${colors.white950};

    &::after {
      opacity: 1;
    }

    /* Keep content above gradient */
    ${ButtonContent} {
      position: relative;
      z-index: 1;
    }

    /* Keep gradient border visible but not animated */
    &::before {
      animation: none;
      z-index: 2;
    }

    /* Make icon darker when active */
    ${AI_GRADIENT_SELECTOR} {
      color: ${colors.orange600} !important;
    }

    ${AI_GRADIENT_SELECTOR}::after {
      opacity: 0;
      animation: none;
    }

    &:hover:not(:disabled)::after {
      background: ${aiSubtleGradientDarkCSS()};
    }
  }

  &:disabled,
  &[aria-disabled] {
    &::before {
      display: none;
    }

    ${AI_GRADIENT_SELECTOR} {
      color: ${colors.iconActionDisabled} !important;
    }

    ${AI_GRADIENT_SELECTOR}::after {
      opacity: 0;
      animation: none;
    }
  }

  /* Hover state - animate the gradient border */
  &:hover:not(:disabled):not(:active):not([aria-pressed='true']) {
    background: ${colors.surfaceDefaultSubtle};

    &::before {
      animation: ${animateBackgroundPosition} 2s linear infinite;
    }

    ${AI_GRADIENT_SELECTOR}::after {
      opacity: 1;
      animation: ${animateAiIconGradientFill} 2s ease-in-out infinite;
    }
  }

  ${({ $isLoading }) =>
    $isLoading &&
    css`
      pointer-events: none;

      ${ButtonContent} {
        visibility: hidden;
      }

      ${ButtonLoadingIconWrapper} {
        color: ${colors.grey500};
      }
    `}
`;

export const AiButton = forwardRef<
  HTMLButtonElement,
  Omit<ButtonProps, 'buttonType'>
>(({ children, ...props }, ref) => (
  <Button ref={ref} buttonType={ButtonType.AI} {...props}>
    <AiIconGradientAnimated />
    {children}
  </Button>
));

AiButton.displayName = `AiButton`;
